import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { EMOJI_RENDERER_TYPES } from "@circle-react-shared/EmojiRenderer/constants";
import { Icon } from "@circle-react-shared/Icon";
import type { EmojiPickerAddOns } from "@circle-react-uikit/EmojiPickerV3";
import {
  CUSTOM_ICON_DARK_DATA_SET,
  CUSTOM_ICON_LIGHT_DATA_SET,
} from "@circle-react-uikit/EmojiPickerV3/data";
import type { CustomEmoji } from "@circle-react-uikit/EmojiPickerV3/data/utils";
import { customEmojiUrl } from "@circle-react-uikit/EmojiPickerV3/data/utils";
import { FormEmojiPicker } from "@circle-react-uikit/Form/FormEmojiPickerV3";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { useIsWithinCompassInputPrefix } from "@/react/components/SettingsApp/CompassCompat/InputPrefixContext";
import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { APPEARANCE, useThemeContext } from "@/react/providers/Theme";

import { DEFAULT_ICON } from "../FormEmojiPickerV3/constants";
import { isDefaultIcon } from "./helpers";

const EMOJI_FIELD_NAME = "emoji";

interface DefaultIcon {
  emoji_type: string;
  identifier: string;
}

export interface EmojiPickerWrapperProps {
  className?: string;
  defaultIcon?: DefaultIcon | null;
  emojiRendererTypes?: Record<string, EmojiPickerAddOns>;
  enableIcons?: boolean;
  name?: string;
  record?: Record<string, any>;
  resetToDefaultIcon?: boolean;
  shouldEmojiPickerRenderAsPortal?: boolean;
  wrapperClassName?: string;
}

export const EmojiPickerWrapper = ({
  name = "custom_emoji",
  record,
  className,
  wrapperClassName,
  shouldEmojiPickerRenderAsPortal = true,
  resetToDefaultIcon = true,
  defaultIcon = DEFAULT_ICON,
  emojiRendererTypes = EMOJI_RENDERER_TYPES,
  enableIcons = false,
}: EmojiPickerWrapperProps) => {
  const { isCircleV4 } = useCircleV4();
  const { isDarkAppearance, currentAppearance } = useThemeContext();
  const { setValue, watch, resetField } = useFormContext();
  const isWithinCompassInputPrefix = useIsWithinCompassInputPrefix();

  const customEmoji = watch(name) as CustomEmoji;
  const recordEmoji = watch(EMOJI_FIELD_NAME);
  const shouldHideRemoveIcon =
    isCircleV4 &&
    resetToDefaultIcon &&
    isDefaultIcon({
      customEmoji,
      defaultIcon,
      isRecordDefaultIcon: record?.is_default_icon,
    });

  const handleEmojiIconUpload = useCallback(
    ({
      status,
      response,
    }: {
      status: string;
      response: {
        signed_id: string;
        filename: string;
      } | null;
    }) => {
      if (status === "completed" && response !== null) {
        setValue(
          name,
          {
            signed_id: response.signed_id,
            filename: response.filename,
            emoji_type: emojiRendererTypes.custom,
          },
          { shouldDirty: true },
        );
      }
    },
    [emojiRendererTypes.custom, name, setValue],
  );

  const handleRemoveEmoji = () => {
    if (resetToDefaultIcon) {
      return resetField(name, {
        defaultValue: defaultIcon,
      });
    }
    resetEmoji();
  };

  const resetEmoji = () => {
    // Clear the field whether or not it is registered in the form.
    setValue(EMOJI_FIELD_NAME, null, { shouldDirty: true });
    resetField(name, {
      defaultValue: {
        emoji: null,
        emoji_type: null,
        identifier: null,
      },
    });
  };

  const pickerAddOns: EmojiPickerAddOns[] = Object.values(
    emojiRendererTypes,
  ).filter(value => enableIcons || value !== emojiRendererTypes.icon);

  return (
    <FormEmojiPicker
      name={name}
      target={
        <EmojiRenderer
          wrapperClassName={classNames(
            isWithinCompassInputPrefix
              ? "flex items-center justify-center"
              : legacyClassNames("form-control__prefix__children"),
            wrapperClassName,
          )}
          className={classNames("!w-6 !p-0", className)}
          emptyIcon={<Icon type="smile" />}
          emoji={customEmoji?.emoji || recordEmoji}
          customEmojiUrl={customEmojiUrl({
            customEmoji,
            appearance: APPEARANCE.LIGHT,
            defaultUrl: record?.custom_emoji_url,
          })}
          customEmojiDarkUrl={customEmojiUrl({
            customEmoji,
            appearance: APPEARANCE.DARK,
            defaultUrl: record?.custom_emoji_dark_url,
          })}
          appearance={currentAppearance}
          isWrapperElementClickable
        />
      }
      pickerAddOns={pickerAddOns}
      popoverRootClassName={isWithinCompassInputPrefix ? "flex" : ""}
      customIcons={
        isDarkAppearance
          ? CUSTOM_ICON_DARK_DATA_SET
          : CUSTOM_ICON_LIGHT_DATA_SET
      }
      onRemoveEmoji={shouldHideRemoveIcon ? undefined : handleRemoveEmoji}
      handleCustomIconUpload={handleEmojiIconUpload}
      shouldEmojiPickerRenderAsPortal={shouldEmojiPickerRenderAsPortal}
    />
  );
};
